/**
 * Fuzzy filtering for the model picker — a typed subsequence matcher with a
 * small scoring pass, so "gpt4o" finds "gpt-4o" and "4o" finds it first among
 * dozens of ids.
 *
 * Deliberately dependency-free (AGENTS.md §42): the problem is fifty model ids
 * in a dropdown, not a search engine. Rules, in the order they dominate:
 *
 *   - a query is a **subsequence** of the candidate (case-insensitive) or the
 *     candidate does not match at all;
 *   - a longer **consecutive run** beats characters picked apart — each
 *     character that continues a run adds more than starting a new match;
 *   - a match that starts at a word boundary (`-`, `_`, `.`, `/`, space or
 *     the start of the id) beats one that starts mid-word;
 *   - earlier and shorter win the remaining ties.
 */

const WORD_BOUNDARY = new Set(["-", "_", ".", "/", " ", ":"]);

/** Score `candidate` against `query`, or `null` when it does not match. */
export function fuzzyScore(query: string, candidate: string): number | null {
  if (query === "") {
    return 0;
  }

  const haystack = candidate.toLowerCase();
  const needle = query.toLowerCase();

  let score = 0;
  let haystackIndex = 0;
  let previousMatch = -2;

  for (const character of needle) {
    const found = haystack.indexOf(character, haystackIndex);

    if (found === -1) {
      return null;
    }

    // Continuing a run is the strongest single signal, a boundary start the
    // next — so "4o" points at the `gpt-4o` family, not at a `-4office`
    // suffix, and "qwen" stays glued together across "qwen-max".
    if (found === previousMatch + 1) {
      score += 10;
    } else if (found === 0 || WORD_BOUNDARY.has(haystack[found - 1] ?? "")) {
      score += 8;
    }

    score -= found * 0.1;
    score -= candidate.length * 0.01;

    haystackIndex = found + 1;
    previousMatch = found;
  }

  return score;
}

/**
 * The candidates that match `query`, best first.
 *
 * An empty query keeps the input order — the "browse everything" case. Ties
 * keep the source order, so a provider's own sort survives filtering.
 */
export function fuzzyFilter<T>(query: string, items: readonly T[], text: (item: T) => string): T[] {
  if (query === "") {
    return [...items];
  }

  return items
    .map((item, index) => ({ item, index, score: fuzzyScore(query, text(item)) }))
    .filter((entry): entry is { item: T; index: number; score: number } => entry.score !== null)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .map((entry) => entry.item);
}
